<template>
  <div>
    <nav aria-label="Primary menu">
      <!-- Frozen header: stays put while the list below scrolls. -->
      <ul class="menu-primary menu-primary-header">
        <li class="menu-item non-draggable" @click.stop="toggleAddMenu">
          <div class="plusButton">
            <a
              ref="newButtonEl"
              class="menu-name"
              :class="{ icon: !isMenuExpanded }"
              draggable="false"
              @dragstart.prevent
              @mouseenter="showTooltip"
              @mouseleave="hideTooltip"
              @mousedown="hideTooltip"
            >
              <PlusIcon /> <span class="name">New</span>
            </a>
          </div>
        </li>
        <MenuItem :route="searchItem" @click="$emit('search')" />
      </ul>

      <div
        ref="menuScrollEl"
        class="menu-scroll"
        :class="{ 'fade-top': fadeTop, 'fade-bottom': fadeBottom }"
        @scroll.passive="updateScrollFades"
        @click.capture="onClickCapture"
      >
        <BaseReorderableList
          :data="draggableGlobalItems"
          tag="div"
          class="menu-primary-list-container"
          root-class="menu-primary menu-primary-list"
          :item-key="(item: ApiMenuItem) => getMenuItemKey(item)"
          :disabled="isSaving"
          @change="handleGlobalReorder"
          @drag-start="onDragStart"
          @drag-end="onDragEnd"
        >
          <template #item="{ item: item }">
            <InboxMenuItem v-if="isInbox(item)" :route="item" />
            <MenuItem v-else :route="item" />
          </template>
        </BaseReorderableList>

        <draggable
          :list="draggableOrgSections"
          item-key="orgId"
          :animation="200"
          :force-fallback="true"
          :fallback-tolerance="5"
          fallback-class="drag-fallback"
          ghost-class="drag-ghost"
          :fallback-on-body="true"
          handle=".menu-org-header"
          @end="handleOrgSectionsReorder"
        >
          <template #item="{ element: section }">
            <MenuOrgSection
              :key="section.orgId"
              :org-id="section.orgId"
              :merged-items="section.mergedItems"
              :is-collapsed="isSectionCollapsed(section.orgId)"
              :is-saving="isSaving"
              @toggle-collapsed="toggleSectionCollapsed(section.orgId)"
              @reorder-merged="
                (newItems) => handleMergedReorder(section.orgId, newItems)
              "
              @drag-start="onDragStart"
              @drag-end="onDragEnd"
            />
          </template>
        </draggable>
      </div>
    </nav>
    <!-- Outside the nav: its scroll-fade mask would clip the popup. -->
    <TypeDatabasePicker ref="typePickerEl" writable-only />
  </div>
</template>

<script lang="ts">
import { addMinutes } from "date-fns";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import draggable from "vuedraggable/src/vuedraggable";

import type {
  MenuItem as ApiMenuItem,
  GraphDatabase,
  OrgId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseReorderableList from "@/components/base/BaseReorderableList.vue";
import { defaultConsoleKindOf } from "@/components/console/default";
import PlusIcon from "@/components/icons/PlusIcon.vue";
import * as inject from "@/components/inject";
import InboxMenuItem from "@/components/menu/InboxMenuItem.vue";
import MenuItem from "@/components/menu/MenuItem.vue";
import {
  createUiMenuItem,
  getMenuItemKey,
  UI_MENU_ITEMS,
} from "@/components/menu/menuItems";
import MenuOrgSection from "@/components/menu/MenuOrgSection.vue";
import { mergeReorderedMenuItemsForReplace } from "@/components/menu/menuReorder";
import { setOrgOrder } from "@/components/menu/useMenuSections";
import { openDefaultCalendarModal } from "@/components/modals/default-calendar";
import { createObject } from "@/components/modals/object";
import { createPage } from "@/components/modals/page";
import { createTask } from "@/components/modals/task";
import { TooltipContent, useTooltip } from "@/components/tooltip";
import TypeDatabasePicker from "@/components/TypeDatabasePicker.vue";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { fetchDefaultCalendar } from "@/composables/useCalendars";
import { RouteNames } from "@/routes/types";
import { placementForDir } from "@/utils/direction";
import { hotkeys } from "@/utils/hotkeys";
import { in_electron } from "@/utils/in";
import { isJsonRecord, type JsonValue } from "@/utils/json";
import { keymap } from "@/utils/keymap";
import {
  navigateTo,
  navigateToCalendar,
  navigateToDatabase,
  navigateToId,
  navigateToObject,
} from "@/utils/navigate";

export default defineComponent({
  components: {
    BaseReorderableList,
    draggable,
    InboxMenuItem,
    PlusIcon,
    MenuItem,
    MenuOrgSection,
    TypeDatabasePicker,
  },
  emits: {
    search: () => true,
  },
  setup() {
    const controller = inject.controller();
    const baseLogger = inject.logger();
    const logger = baseLogger.forModule("MenuPrimary");
    const profile = inject.profile();
    const typePickerEl = ref<InstanceType<typeof TypeDatabasePicker> | null>(
      null
    );

    const menu = inject.menu();
    const isMenuExpanded = menu.isExpanded;
    const isSaving = ref(false);
    const isDragging = ref(false);
    const suppressClicksUntilMs = ref<number>(0);

    // The sidebar's collapsed org sections, keyed by org id (expanded by
    // default), in the account-namespaced browser store — one persisted
    // record owned here for all the MenuOrgSection children (each gets its
    // `isCollapsed` and emits `toggleCollapsed`). The state also exists as
    // the legacy global "menu-org-section-collapsed" key, written raw to
    // localStorage by older versions; the first read migrates it.
    const browserStore = useBrowserStore(profile.value.user.accountId);
    browserStore.migrate("menu:state", "menu-org-section-collapsed");
    const collapsedSections = browserStore.syncState<Record<string, JsonValue>>(
      "menu:state",
      {},
      { deep: true }
    );
    // The stored value is untrusted JSON: reset anything but a record.
    if (!isJsonRecord(collapsedSections.value)) collapsedSections.value = {};
    onBeforeUnmount(collapsedSections.release);
    const isSectionCollapsed = (orgId: OrgId) =>
      collapsedSections.value[orgId] === true;
    const toggleSectionCollapsed = (orgId: OrgId) => {
      collapsedSections.value[orgId] = !isSectionCollapsed(orgId);
    };

    const open = async (database: string, workspaceId: WorkspaceId) => {
      const modal = createObject(baseLogger);
      try {
        const result = await modal.openObjectModal(
          database,
          workspaceId,
          controller,
          profile
        );
        if (result?.shouldRedirect && result.objectId) {
          navigateToId(
            router,
            controller,
            {},
            workspaceId,
            database,
            result.objectId
          );
        }
      } finally {
        modal.release();
      }
    };

    const { globalItems, orgSections } = menu;
    const renderedGlobalItems = computed(() => globalItems.value);

    // Local reorderable copy of global (app_screen) items
    const draggableGlobalItems = ref<Array<{ items: ApiMenuItem[] }>>([
      { items: [] },
    ]);
    watch(
      renderedGlobalItems,
      (items) => {
        draggableGlobalItems.value[0].items = [...items];
      },
      { immediate: true }
    );

    // Local reorderable copy of org sections (for drag-to-reorder)
    const draggableOrgSections = ref<
      Array<{
        orgId: OrgId;
        mergedItems: ApiMenuItem[];
      }>
    >([]);
    watch(
      orgSections,
      (sections) => {
        draggableOrgSections.value = sections.map((s) => ({
          orgId: s.orgId,
          mergedItems: s.mergedItems,
        }));
      },
      { immediate: true }
    );

    const handleOrgSectionsReorder = () => {
      setOrgOrder(draggableOrgSections.value.map((s) => s.orgId));
    };

    // Check if item is the Inbox app screen
    const isInbox = (item: ApiMenuItem): boolean => {
      return item.target.kind === "app_screen" && item.target.value === "inbox";
    };

    const router = inject.router();

    const newTask = async () => {
      const modal = createTask();
      try {
        const result = await modal.openTaskModal(controller, profile);
        if (result?.shouldRedirect && result.taskId) {
          await navigateToObject(router, controller, {
            kind: "task",
            id: result.taskId,
          });
        }
      } finally {
        modal.release();
      }
    };

    const newEvent = async () => {
      const now = new Date();
      const { calendar: defaultCal, issue } =
        await fetchDefaultCalendar(controller);
      if (!defaultCal) {
        logger.warn("New event: no default calendar, opening the calendar");
        await navigateTo(RouteNames.Calendar, router);
        return;
      }
      if (issue) {
        // Don't create a draft that would be deleted right away (the grid
        // drops events of disabled calendars): explain and offer the fix.
        logger.warn("New event: default calendar issue", {
          issue,
          calendar: defaultCal.id,
        });
        openDefaultCalendarModal(issue);
        return;
      }
      const draftEventId = await controller.event.create({
        title: "",
        calendar: defaultCal.id,
        draft: true,
        time: { start_time: now, end_time: addMinutes(now, 60) },
        attendees: [],
      });
      logger.info("Created draft event from the + menu", {
        eventId: draftEventId,
        calendar: defaultCal.id,
      });
      await navigateToCalendar(
        router,
        now,
        undefined,
        draftEventId || undefined
      );
    };

    const newPage = async () => {
      const modal = createPage(controller, baseLogger, profile);
      try {
        const result = await modal.openPageModal();
        if (result?.shouldRedirect && result.pageId) {
          await navigateToObject(router, controller, {
            kind: "page",
            id: result.pageId,
          });
        }
      } finally {
        modal.release();
      }
    };

    const newButtonEl = ref<HTMLAnchorElement>();

    // The + New picker: picking a default type opens its dedicated creation
    // flow, custom types open the generic object modal, and the trailing
    // database icon navigates to the type's database screen.
    const onNewTypePicked = async (database: GraphDatabase | null) => {
      if (!database) return;
      const kind = defaultConsoleKindOf(database);
      if (kind === "task") await newTask();
      else if (kind === "event") await newEvent();
      else if (kind === "page") await newPage();
      else await open(database.id, database.workspace);
    };
    const showAddMenu = () => {
      void typePickerEl.value?.showPicker(
        newButtonEl.value,
        (database) => void onNewTypePicked(database),
        {
          placement: placementForDir("right-start"),
          placeholder: "Search types…",
          onDatabaseOpen: (database) =>
            void navigateToDatabase(router, database.workspace, database.id),
        }
      );
    };
    const toggleAddMenu = () => {
      const picker = typePickerEl.value;
      if (!picker) return;
      if (picker.isDisplayed) picker.closePicker();
      else showAddMenu();
    };
    const addItemTooltip = computed<TooltipContent | undefined>(() => {
      if (isMenuExpanded.value) return undefined;
      if (!in_electron) return undefined;
      return {
        text: "Create object",
        shortcut:
          hotkeys.format.stringify(
            [keymap.keyMod, keymap.keyN],
            keymap.formatSymbol,
            keymap.separatorSpace
          ) || undefined,
      };
    });
    const {
      showTooltip,
      hideTooltip,
      release: releaseTooltip,
    } = useTooltip(newButtonEl, addItemTooltip, "right");
    onBeforeUnmount(releaseTooltip);
    watch(
      isMenuExpanded,
      (expanded) => {
        if (expanded) {
          hideTooltip();
        }
      },
      { immediate: true }
    );

    const handleGlobalReorder = async () => {
      if (!isSaving.value) {
        isSaving.value = true;
        try {
          await controller.menuItems.replace(
            mergeReorderedMenuItemsForReplace({
              currentItems: menu.visibleItems.value,
              previousManagedItems: renderedGlobalItems.value,
              reorderedManagedItems: draggableGlobalItems.value[0].items,
            })
          );
        } catch (error) {
          window.console.error("Failed to save menu order:", error);
          draggableGlobalItems.value[0].items = [...renderedGlobalItems.value];
        } finally {
          isSaving.value = false;
        }
      }
    };

    // Reorder one organization subset while preserving every controller item
    // managed by another section.
    const handleMergedReorder = async (
      orgId: OrgId,
      newItems: ApiMenuItem[]
    ) => {
      if (isSaving.value) return;
      isSaving.value = true;
      const previousManagedItems =
        orgSections.value.find((section) => section.orgId === orgId)
          ?.mergedItems ?? [];
      try {
        await controller.menuItems.replace(
          mergeReorderedMenuItemsForReplace({
            currentItems: menu.visibleItems.value,
            previousManagedItems,
            reorderedManagedItems: newItems,
          })
        );
      } catch (error) {
        window.console.error("Failed to save menu order:", error);
      } finally {
        isSaving.value = false;
      }
    };

    const onDragStart = () => {
      isDragging.value = true;
    };
    const onDragEnd = () => {
      isDragging.value = false;
      // Prevent the "mouseup -> click" that would otherwise navigate
      suppressClicksUntilMs.value = Date.now() + 350;
      updateScrollFades();
    };
    const onClickCapture = (event: MouseEvent) => {
      if (isDragging.value || Date.now() < suppressClicksUntilMs.value) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const searchItem = createUiMenuItem(UI_MENU_ITEMS.Search);

    // Scroll-edge fades used to be CSS scroll-timeline animations on a
    // mask-image. Those sometimes stuck mid-fade (inactive timeline / fill
    // mode), washing out items under New/Search even at rest. Drive the
    // mask from the real scroll position instead.
    const menuScrollEl = ref<HTMLElement | null>(null);
    const fadeTop = ref(false);
    const fadeBottom = ref(false);
    const updateScrollFades = () => {
      const el = menuScrollEl.value;
      if (!el) {
        fadeTop.value = false;
        fadeBottom.value = false;
        return;
      }
      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 1) {
        fadeTop.value = false;
        fadeBottom.value = false;
        return;
      }
      fadeTop.value = el.scrollTop > 1;
      fadeBottom.value = el.scrollTop < maxScroll - 1;
    };
    watch([globalItems, orgSections, isMenuExpanded], updateScrollFades, {
      flush: "post",
    });
    let scrollFadeObserver: ResizeObserver | undefined;
    let scrollFadeMutations: MutationObserver | undefined;
    const observeScrollFadeChildren = (el: HTMLElement) => {
      if (!scrollFadeObserver) return;
      for (const child of el.children) scrollFadeObserver.observe(child);
    };
    onMounted(() => {
      updateScrollFades();
      const el = menuScrollEl.value;
      if (!el || typeof ResizeObserver === "undefined") return;
      scrollFadeObserver = new ResizeObserver(() => updateScrollFades());
      scrollFadeObserver.observe(el);
      observeScrollFadeChildren(el);
      if (typeof MutationObserver !== "undefined") {
        scrollFadeMutations = new MutationObserver(() => {
          observeScrollFadeChildren(el);
          updateScrollFades();
        });
        scrollFadeMutations.observe(el, { childList: true });
      }
    });
    onBeforeUnmount(() => {
      scrollFadeObserver?.disconnect();
      scrollFadeMutations?.disconnect();
    });

    return {
      RouteNames,
      searchItem,
      draggableGlobalItems,
      getMenuItemKey,
      draggableOrgSections,
      handleGlobalReorder,
      isSectionCollapsed,
      toggleSectionCollapsed,
      handleMergedReorder,
      handleOrgSectionsReorder,
      hideTooltip,
      isInbox,
      isMenuExpanded,
      isSaving,
      onClickCapture,
      onDragEnd,
      onDragStart,
      newButtonEl,
      menuScrollEl,
      fadeTop,
      fadeBottom,
      updateScrollFades,
      showAddMenu,
      showTooltip,
      toggleAddMenu,
      typePickerEl,
    };
  },
});
</script>

<style scoped lang="scss">
nav {
  max-height: 100%;
  display: flex;
  flex-direction: column;
  // Was the list container's padding-top before the header was frozen.
  padding-top: 40px;
  box-sizing: border-box;
}

.menu-primary-header {
  flex-shrink: 0;
}

.menu-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;

  // Soft edge fades only while there is overflow in that direction — avoids
  // the old scroll-timeline mask sticking mid-fade at rest.
  &.fade-top {
    mask-image: linear-gradient(to bottom, transparent 0%, black 40px);
  }

  &.fade-bottom {
    mask-image: linear-gradient(
      to bottom,
      black calc(100% - 40px),
      transparent
    );
  }

  &.fade-top.fade-bottom {
    mask-image: linear-gradient(
      to bottom,
      transparent 0%,
      black 40px,
      black calc(100% - 40px),
      transparent 100%
    );
  }
}

:deep(.reorderable-section) {
  margin-bottom: 0;
}

.plusButton {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 12px;
}

.menu-primary-list-container {
  padding-bottom: 8px;
}
</style>
